/**
 * Notification Service
 * Handles in-app notifications for payment requests
 */

export interface Notification {
  id: string;
  type: 'payment_request' | 'payment_received' | 'appointment_reminder' | 'general';
  title: string;
  message: string;
  data?: any;
  timestamp: string;
  read: boolean;
  userId: string; // Customer ID
}

export interface PaymentRequestNotification {
  appointmentId: string;
  amount: number;
  serviceName: string;
  businessName: string;
  paymentMethod: 'venmo' | 'paypal' | 'sms' | 'email';
  paymentLink?: string;
}

class NotificationService {
  private notifications: Notification[] = [];
  private listeners: ((notifications: Notification[]) => void)[] = [];

  /**
   * Add a notification
   */
  addNotification(notification: Omit<Notification, 'id' | 'timestamp' | 'read'>): string {
    const newNotification: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      read: false,
    };

    this.notifications.unshift(newNotification); // Add to beginning
    this.notifyListeners();
    
    console.log('🔔 Notification added:', newNotification);
    return newNotification.id;
  }

  /**
   * Get notifications for a user
   */
  getNotifications(userId: string): Notification[] {
    return this.notifications.filter(n => n.userId === userId);
  }

  /**
   * Get unread notifications for a user
   */
  getUnreadNotifications(userId: string): Notification[] {
    return this.notifications.filter(n => n.userId === userId && !n.read);
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.notifyListeners();
      console.log('✅ Notification marked as read:', notificationId);
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  markAllAsRead(userId: string): void {
    this.notifications
      .filter(n => n.userId === userId && !n.read)
      .forEach(n => n.read = true);
    this.notifyListeners();
    console.log('✅ All notifications marked as read for user:', userId);
  }

  /**
   * Delete notification
   */
  deleteNotification(notificationId: string): void {
    const index = this.notifications.findIndex(n => n.id === notificationId);
    if (index > -1) {
      this.notifications.splice(index, 1);
      this.notifyListeners();
      console.log('🗑️ Notification deleted:', notificationId);
    }
  }

  /**
   * Add payment request notification
   */
  addPaymentRequestNotification(
    customerId: string,
    paymentRequest: PaymentRequestNotification
  ): string {
    const title = `Payment Request from ${paymentRequest.businessName}`;
    const message = `Please pay $${paymentRequest.amount} for ${paymentRequest.serviceName}`;

    return this.addNotification({
      type: 'payment_request',
      title,
      message,
      data: paymentRequest,
      userId: customerId,
    });
  }

  /**
   * Add payment received notification
   */
  addPaymentReceivedNotification(
    businessId: string,
    amount: number,
    customerName: string,
    paymentMethod: string
  ): string {
    const title = 'Payment Received!';
    const message = `Received $${amount} from ${customerName} via ${paymentMethod}`;

    return this.addNotification({
      type: 'payment_received',
      title,
      message,
      data: { amount, customerName, paymentMethod },
      userId: businessId,
    });
  }

  /**
   * Add appointment reminder notification
   */
  addAppointmentReminderNotification(
    customerId: string,
    appointmentDate: string,
    appointmentTime: string,
    serviceName: string,
    businessName: string
  ): string {
    const title = 'Appointment Reminder';
    const message = `Your ${serviceName} appointment at ${businessName} is tomorrow at ${appointmentTime}`;

    return this.addNotification({
      type: 'appointment_reminder',
      title,
      message,
      data: { appointmentDate, appointmentTime, serviceName, businessName },
      userId: customerId,
    });
  }

  /**
   * Subscribe to notification changes
   */
  subscribe(listener: (notifications: Notification[]) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }

  /**
   * Get notification count for user
   */
  getNotificationCount(userId: string): number {
    return this.getUnreadNotifications(userId).length;
  }

  /**
   * Clear all notifications for user
   */
  clearAllNotifications(userId: string): void {
    this.notifications = this.notifications.filter(n => n.userId !== userId);
    this.notifyListeners();
    console.log('🗑️ All notifications cleared for user:', userId);
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;